import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'

export interface AdminToolIngredient {
  id: number
  productId: number
  productName: string
  productNameNumber: string
  categoryId: number | null
  countryId: number
}

interface AdminToolIngredientsState {
  ingredients: AdminToolIngredient[]
  loading: boolean
  updating: boolean
  success: string | null
  error: string | null
}

export interface UpdateIngredientMetadataPayload {
  id: number
  data: {
    productName?: string
    categoryId?: number | null
  }
}

const initialState: AdminToolIngredientsState = {
  ingredients: [],
  loading: false,
  updating: false,
  success: null,
  error: null
}

export const fetchAdminToolIngredients = createAsyncThunk<AdminToolIngredient[], void, Redux>(
  'adminToolsIngredients/fetch',
  async (_, { extra }) => {
    const { data } = await extra.httpClient.get('cerebro-product-companies')

    if (Array.isArray(data)) {
      return data
    }

    if (Array.isArray(data?.data)) {
      return data.data
    }

    return []
  }
)

export const updateAdminToolIngredientMetadata = createAsyncThunk<
  AdminToolIngredient,
  UpdateIngredientMetadataPayload,
  Redux
>('adminToolsIngredients/updateMetadata', async ({ id, data }, { extra }) => {
  const { data: responseData } = await extra.httpClient.put(`cerebro-product-companies/${id}/metadata`, data)

  return responseData?.data ?? responseData
})

const adminToolsIngredientsSlice = createSlice({
  name: 'adminToolsIngredients',
  initialState,
  reducers: {
    clearAdminToolIngredientsFeedback: state => {
      state.success = null
      state.error = null
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchAdminToolIngredients.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchAdminToolIngredients.fulfilled, (state, action) => {
      state.loading = false
      state.ingredients = action.payload
    })
    builder.addCase(fetchAdminToolIngredients.rejected, (state, action) => {
      state.loading = false
      state.error = action.error.message || 'Failed to fetch ingredients'
    })

    builder.addCase(updateAdminToolIngredientMetadata.pending, state => {
      state.updating = true
      state.error = null
      state.success = null
    })
    builder.addCase(updateAdminToolIngredientMetadata.fulfilled, (state, action) => {
      state.updating = false
      state.success = 'Ingredient updated successfully'
      state.ingredients = state.ingredients.map(item => (item.id === action.payload.id ? action.payload : item))
    })
    builder.addCase(updateAdminToolIngredientMetadata.rejected, (state, action) => {
      state.updating = false
      state.error = action.error.message || 'Failed to update ingredient'
    })
  }
})

export const { clearAdminToolIngredientsFeedback } = adminToolsIngredientsSlice.actions

export default adminToolsIngredientsSlice.reducer
